import { useAuth0 } from '@auth0/auth0-react';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UserAuthenticationService } from '../types/UserAuthenticationService';

type Auth0HandlerProps = {
  UserAuthenticationService: UserAuthenticationService;
};

export function Auth0Handler({
  UserAuthenticationService,
  children,
}: PropsWithChildren<Auth0HandlerProps>) {
  const {
    user,
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  // OHIF 3.9 made these regular (unbound) methods; destructuring them off the
  // service loses `this` (they use `this.serviceImplementation`). Bind once so
  // the identities stay stable across renders (used in the deps below).
  const {
    set: setAuthenticationService,
    setUser,
    setServiceImplementation,
    getUser,
  } = useMemo(
    () => ({
      set: UserAuthenticationService.set.bind(UserAuthenticationService),
      setUser: UserAuthenticationService.setUser.bind(UserAuthenticationService),
      setServiceImplementation:
        UserAuthenticationService.setServiceImplementation.bind(
          UserAuthenticationService
        ),
      getUser: UserAuthenticationService.getUser.bind(UserAuthenticationService),
    }),
    [UserAuthenticationService]
  );

  // Latest access token, kept independent of the React auth-provider state. The
  // provider's `user` is reset by the label datasource's full-page reload
  // (`window.location.search=`), so early item-load requests (observations /
  // characterisations, fired from useTaskLoader) could hit `getAuthorizationHeader`
  // while `getUser()` was momentarily null or `isAuthenticated` was mid-flip →
  // empty header → 401. The ref survives those windows.
  const tokenRef = useRef<string | null>(null);

  const refreshToken = useCallback(
    async function () {
      if (isLoading || !isAuthenticated) {
        return;
      }

      const token = await getAccessTokenSilently();
      tokenRef.current = token;
      const serviceUser = getUser();
      if (!serviceUser || serviceUser.access_token !== token) {
        setUser({
          access_token: token,
          ...user,
        });
      }
    },
    [getAccessTokenSilently, isAuthenticated, isLoading, setUser, user, getUser]
  );

  const handleUnauthenticated = useCallback(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}`,
        },
      });
    }

    // return null because this is used in a react component
    return null;
  }, [
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    window.location.pathname,
    window.location.search,
  ]);

  const getAuthorizationHeader = useCallback(() => {
    // Prefer the ref (always the latest token once Auth0 has issued one); fall
    // back to the provider user. Gating on `isAuthenticated`/`getUser()` here was
    // what dropped the header on early item-load requests → 401.
    const token = tokenRef.current ?? getUser()?.access_token;
    if (!token) {
      return {};
    }

    // Kick off a background refresh so the next call has a fresh token. Be sure
    // the Auth0 configuration does not invalidate the previous token.
    refreshToken();

    return {
      Authorization: `Bearer ${token}`,
    };
  }, [getUser, refreshToken]);

  useEffect(() => {
    refreshToken();
  }, [refreshToken]);

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    setAuthenticationService({ enabled: true });
    setServiceImplementation({
      getAuthorizationHeader,
      handleUnauthenticated,
    });
    setInitialized(true);
  }, [
    setAuthenticationService,
    setServiceImplementation,
    getAuthorizationHeader,
    handleUnauthenticated,
  ]);

  // To avoid useless refresh while Auth0 is fetching user, we render nothing
  if (isLoading || !initialized) {
    return null;
  }

  // Fragment is here to make TypeScript happy in "./Provider.tsx"
  return <>{children}</>;
}
